import React, { useState, useEffect, useContext } from "react";
import { DataContext } from "../../context/DataContext";
import {
  Flex,
  Text,
  Group,
  Button,
  Container,
  Stack,
  Paper,
  Divider,
  Center,
  Loader,
  Alert,
} from "@mantine/core";
import QRCode from "react-qr-code";
import { IconDeviceMobileBolt } from "@tabler/icons-react";
import AppIcon from "../../components/AppIcon";
import useStatusCheck from "../../hooks/useStatusCheck";
import useEdgeStatusCheck from "../../hooks/useEdgeStatusCheck";
import { useNavigate } from "react-router-dom";
export default function Handoff({ nextPath, urlOverride }) {
  const { settings, setSessionState, dispatch, text, data } = useContext(DataContext);
  const [url, setUrl] = useState("");
  const [qrFound, setQrFound] = useState(false);
  const [isTimeUp, setIsTimeUp] = useState(false);
  const [shouldFetch, setShouldFetch] = useState(true);
  const [status, setStatus] = useState("NEW");
  const [shouldGetQrStatus, setShouldGetQrStatus] = useState(true);
  const navigate = useNavigate();
  
  // Use edge status check when edge is true
  const isEdgeMode = data?.edge === true;
  useStatusCheck(!isEdgeMode && qrFound, true, "handoff");
  useEdgeStatusCheck(isEdgeMode && qrFound);
  useEffect(() => {
    // Get all current URL query parameters
    const urlParams = new URLSearchParams(window.location.search);
    const frontendParam = urlParams.get('frontend');
    const edgeParam = urlParams.get('edge');
    
    // Preserve all existing query parameters
    urlParams.set('qr', 'true');
    if (nextPath) {
      urlParams.set('nextPath', nextPath);
    }
    // Preserve edge parameter if it exists
    if (edgeParam) {
      urlParams.set('edge', edgeParam);
    }
    const queryString = urlParams.toString();
    
    const isLocalhost =
      window.location.hostname === "localhost" ||
      window.location.hostname.startsWith("127.0.0.") ||
      window.location.hostname.startsWith("0.0.0.0");

    if (isLocalhost) {
      if (urlOverride) {
        setUrl(urlOverride);
      } else {
        setUrl(
          `https://844198ea0184.ngrok.app/${settings.request_id}?${queryString}`
        );
      }
    } else if (window.location.hostname.endsWith("fe-dcams.pages.dev")) {
      // For Cloudflare Pages preview domains
      setUrl(`${window.location.origin}/${settings.request_id}?${queryString}`);
    } else {
      // Determine frontend to use based on hostname or query param
      const useVxFrontend = window.location.hostname === "vx-fe.dcams.app" || frontendParam === "vx-fe";
      
      const urlToUse = useVxFrontend
          ? `https://vx-fe.dcams.app/${settings.request_id}?${queryString}`
          : `${process.env.REACT_APP_FRONTEND_URL}/${settings.request_id}?${queryString}`;
      setUrl(urlToUse);
    }
  }, [settings, nextPath, urlOverride]);

  useEffect(() => {
    const fetchQrStatus = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/v3/stable/api/frontend/qrStatus`,
          {
            method: "POST",
            body: JSON.stringify({
              request_id: settings.request_id,
              type: "get",
              site_name: settings.site_name,
            }),
          }
        );
        const data = await response.json();

        if (data.found) {
          setQrFound(true); // Stop fetching once QR is found
        }
      } catch (error) {
        console.error("Error fetching QR status:", error);
      }
    };

    if (!qrFound) {
      const intervalId = setInterval(fetchQrStatus, 6000);
      return () => clearInterval(intervalId);
    }
  }, [qrFound, settings]);

  useEffect(() => {
    const timerId = setTimeout(
      () => {
        setIsTimeUp(true);
      },
      5 * 60 * 1000
    ); // 5 minutes

    return () => clearTimeout(timerId);
  }, []);

  const restartFetch = () => {
    setIsTimeUp(false);
    setShouldFetch(true);
    setShouldGetQrStatus(true); // Reset the flag to allow QR status fetching
  };

  if (qrFound) {
    return (
      <Stack
        align="center"
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center", // This will push the button to the bottom
          height: "100%", // This should take the full height of its parent
        }}
      >
        <Loader />
        <Stack spacing={0} align="center">
          <Text fz="xl" fw={700} align="center">
            {text.handoff.inProgress.title}
          </Text>
          <Text c="dimmed" align="center">
            {text.handoff.inProgress.subTitle}
          </Text>
        </Stack>
        <Group noWrap>
          {isTimeUp && (
            <Button variant="outline" onClick={restartFetch}>
              {text.handoff.inProgress.button}
            </Button>
          )}
        </Group>
      </Stack>
    );
  }

  // Conditional banner logic
  const shouldShowBanner = (settings.site_name === "kid-pre-prod" || settings.site_name === "kid-qa") && 
                           settings.journey?.name && 
                           (settings.journey.name === "estimate_only_roc" || settings.journey.name === "estimate_only_yoti");
  
  const getBannerText = () => {
    if (settings.journey?.name === "estimate_only_roc") return "FAE PROVIDER: ROC";
    if (settings.journey?.name === "estimate_only_yoti") return "FAE PROVIDER: Yoti";
    return "";
  };

  return (
    <Stack
      align="center"
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center", // This will push the button to the bottom
        height: "100%", // This should take the full height of its parent
        position: "relative",
      }}
    >
      {shouldShowBanner && (
        <Alert 
          color="blue" 
          style={{ 
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            borderRadius: 0,
            padding: "8px",
            minHeight: "auto"
          }}
        >
          <Text align="center" fw={600} size="sm">
            {getBannerText()}
          </Text>
        </Alert>
      )}
      
      <Stack align="center" mb={20}>
        <AppIcon
          styles={data?.styles ?? settings?.journey?.styles ?? settings?.theme ?? null}
          slot="handoff.header.main"
          boxSize={100}
          fallback={<IconDeviceMobileBolt size={60} stroke={1} color="green" />}
        />
        <Text fz={25} fw={700}>
          {text.handoff.title}
        </Text>
      </Stack>

      <QRCode size={200} value={url} viewBox={`0 0 256 256`} />
      <Stack spacing={0} mt={20} p="sm">
        <Text align="center" fw={600}>
          {text.handoff.subTitle}
        </Text>
        <Text c="dimmed" fz="sm" align="center">
          {text.handoff.explanation}
        </Text>
      </Stack>
      {settings.journey?.settings?.allowUpload && (
        <Button
          mt="lg"
          variant="subtle"
          size="xs"
          onClick={() => navigate("/upload")}
        >
          I would rather continue on this device
        </Button>
      )}

      <Group noWrap>
        {isTimeUp && (
          <Button variant="outline" onClick={restartFetch}>
            {text.handoff.inProgress.button}
          </Button>
        )}
      </Group>
    </Stack>
  );
}
